import {
    HttpEvent,
    HttpHandler,
    HttpInterceptor,
    HttpParams,
    HttpRequest,
    HttpResponse,
} from '@angular/common/http';
import { Inject, Injectable, Optional } from '@angular/core';
import { Observable } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap, tap } from 'rxjs/operators';
import { HeaderConfigService } from '../../services/public-services';
import {
    ADD_REQUESTED_LANGUAGE_HEADER,
    CAAS_REQUEST_HEADER,
    cloneRequestHeaders,
    REQUESTED_LANGUAGE_HEADER,
    TRANSLATE_REQUEST_HEADER,
} from './../../helpers/request-helper/request.helper';

/**
 * Interceptor to find requests with a translate header. After language changes it refreshes this requests.
 */
@Injectable({ providedIn: 'root' })
export class TranslateInterceptor implements HttpInterceptor {
    public constructor(
        private sewConfigService: HeaderConfigService,
        @Optional()
        @Inject(ADD_REQUESTED_LANGUAGE_HEADER)
        private addRequestLanguageHeader: boolean | null
    ) {}

    public intercept<T>(request: HttpRequest<T>, next: HttpHandler): Observable<HttpEvent<T>> {
        if (!request.headers.get(TRANSLATE_REQUEST_HEADER)) {
            return next.handle(request);
        }

        let initialRequest = true;
        let caasRequest = false;
        let xLanguageHeader = this.addRequestLanguageHeader ?? false;

        if (this.isCaasRequest<T>(request)) {
            caasRequest = true;
            xLanguageHeader = false;
        }

        return this.sewConfigService.language.pipe(
            distinctUntilChanged(),
            filter((language) => language.length > 0),
            switchMap((language) =>
                this.createRequest(
                    request,
                    next,
                    initialRequest,
                    caasRequest,
                    xLanguageHeader,
                    language
                ).pipe(
                    tap((event) => {
                        if (event instanceof HttpResponse) {
                            initialRequest = false;
                        }
                    })
                )
            )
        );
    }

    private createRequest<T>(
        request: HttpRequest<T>,
        next: HttpHandler,
        initialRequest: boolean,
        isCaasRequest: boolean,
        xLanguageHeader: boolean,
        language: string
    ) {
        let caasFilterParams = null;
        if (isCaasRequest) {
            const currentFilterParams = request.params.get('filter');

            if (currentFilterParams && currentFilterParams.indexOf('fs_language') > 0) {
                caasFilterParams = new HttpParams().set(
                    'filter',
                    currentFilterParams.replace(
                        /(?!'fs_language':')([A-Z]{2}_[A-Z]{2})/,
                        language.toUpperCase()
                    )
                );
            } else {
                caasFilterParams = new HttpParams();
                if (currentFilterParams) {
                    caasFilterParams = new HttpParams().set('filter', currentFilterParams);
                }
                caasFilterParams = caasFilterParams.append(
                    'filter',
                    `{'fs_language':'${language.toUpperCase()}'}`
                );
            }
        }

        return next
            .handle(
                request.clone({
                    params: caasFilterParams ?? request.params,
                    headers: cloneRequestHeaders(
                        request,
                        xLanguageHeader ? { [REQUESTED_LANGUAGE_HEADER]: language } : {},
                        this.requestHeaderToRemove(isCaasRequest)
                    ),
                })
            )
            .pipe(
                map((event) =>
                    event instanceof HttpResponse && !initialRequest
                        ? event.clone({
                              // add translate header to response to let the consumer know
                              // that the request was triggered by a language change
                              headers: cloneRequestHeaders(event, this.createResponseHeader(isCaasRequest)),
                          })
                        : event
                )
            );
    }

    private createResponseHeader(isCaasRequest: boolean): { [key: string]: string } {
        if (isCaasRequest) {
            return {
                [TRANSLATE_REQUEST_HEADER]: 'true',
                [CAAS_REQUEST_HEADER]: 'true',
            };
        } else {
            return { [TRANSLATE_REQUEST_HEADER]: 'true' };
        }
    }

    private requestHeaderToRemove(isCaasRequest: boolean): string[] {
        if (isCaasRequest) {
            return [TRANSLATE_REQUEST_HEADER, CAAS_REQUEST_HEADER];
        } else {
            return [TRANSLATE_REQUEST_HEADER];
        }
    }

    private isCaasRequest<T>(request: HttpRequest<T>) {
        return request.headers.get(CAAS_REQUEST_HEADER);
    }
}
